/*
GPS DataViz

Copyright © 2021 GPSDataViz

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/

import React, { useState, useEffect } from "react";

import "../../Styles/styles-ResultsTable.css";
import "../../Styles/styles-Example.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import {
  findNearestNumber,
} from "../../Utils/chart_utils";
import BarChartHighcharts from "../Charts/BarChart";
import { stackedBarChartDtoToModel } from "../../mappers/model/StackedBarChartMapper";
import { useMemo } from "react";
import { BodyMapView } from "../BodyMapView/BodyMapView";
import { useSelector } from "react-redux";
import {
  getRestAndTravelDaysInChart,
} from "../Team/TeamAvgCharts";
import moment from "moment";
import { mapDataForHighcharts } from "../../mappers/model/BarChartMapper";

export const addRestAndTravelDates = (data, metrics, restAndTravelDays) => {
  if (!metrics || !data) return [];
  let maxValues = data.reduce((acc, curr) => {
    metrics.forEach((metric) => {
      if (!acc[metric] || curr[metric] > acc[metric])
        acc[metric] = curr[metric];
    });
    return acc;
  }, {});
  let added = data.concat(
    restAndTravelDays.map((day) => {
      return metrics.reduce(
        (acc, curr) => {
          acc[curr] = maxValues[curr];
          return acc;
        },
        { ...day, disp_date: moment(day.date).format("MM-DD") }
      );
    })
  );
  added.sort((a, b) => (a.date < b.date ? -1 : 1));
  return added;
};

export default function SubjectiveCharts({
  dataIn: { data, colors: colors_100, ...dataIn },
  allGames,
  playersPage,
  startDate,
  endDate,
  showTravel,
  showRest,
}) {
  const [wellnessQuestionCharts, setWellnessQestionCharts] = useState(null);
  const {
    info: { restAndTravel, team_name: teamName, team_colors: teamColors, metric: teamMetric,
      acwr_lower_limit: ACWRLowerLimit, acwr_upper_limit: ACWRUpperLimit
    },
    games
  } = useSelector(({ team }) => team);
  const restAndTravelDates = useMemo(
    () =>
      getRestAndTravelDaysInChart(
        startDate,
        endDate,
        Object.values(dataIn.wellness || {})?.[0],
        restAndTravel,
        showRest,
        showTravel
      ),
    [startDate, endDate, restAndTravel, showRest, dataIn, showTravel]
  );

  const getAnswerByValue = (key, value) => {
    const metric = dataIn.wellness_metrics.find((m) => m.key === key);
    if (metric && metric.answers) {
      const [answer, index] = findNearestNumber(metric.answers, value);
      if (answer) {
        return [answer.answer, index];
      }
    }
    return [null];
  };

  const label = useMemo(() => {
    const list = dataIn?.wellness
      ? Object.values(dataIn?.wellness_metrics)
      : [];
    return list.filter(
        (l) =>
          l.key !== "hrv_score" &&
          l.key !== "wellness_score" &&
          l.no_color == false
      );
  }, [dataIn]);



  const mergedData = useMemo(
    () =>
      dataIn?.wellness
        ? Object.values(dataIn.wellness)
            .flat()
            .reduce((merged, obj) => {
              const key = obj.date || obj.disp_date;
              merged[key] = { ...(merged[key] || {}), ...obj };
              return merged;
            }, {})
        : [],
    [dataIn.wellness]
  );

  const dat = useMemo(
    () =>
      Object.values(mergedData)
        .map((item) => {
          const currentObject = data?.find((a) => a.date === item.date);
          const RPE = currentObject?.RPE;
          const SRPE = currentObject?.session_RPE || 0;
          const gameDay = allGames.find((game) => game.date === item.date);

          const newDataObj = { ...item };
          const sum = item.wellness_score;
          return {
            ...newDataObj,
            sum,
            disp_date: gameDay ? gameDay?.opponent : item.disp_date,
            acc: (
              Object.values(item)
                .filter((value) => typeof value === "number" && !isNaN(value))
                .reduce((sum, value) => sum + value, 0) -
              (item?.wellness_score || 0)
            )?.toFixed(2),
            RPE: data?.find((a) => a.date === item.date)?.RPE,
            ComolativeRPE: !isNaN(RPE) ? RPE + (item?.wellness_score || 0) : 0,
            SRPE: isNaN(SRPE) ? 0 : SRPE,
            wellness_score: item["wellness_score"],
          };
        })
        .sort((a, b) => {
          let dateA = new Date(a.date);
          let dateB = new Date(b.date);

          if (dateA < dateB) {
            return -1;
          }
          if (dateA > dateB) {
            return 1;
          }
          return 0;
        }),
    [mergedData, data]
  );

  function findMaxValueObject(array, key) {
    if (array.length === 0) {
      return null; // Handle empty array
    }

    let maxValueObject = array[0];
    for (let i = 1; i < array.length; i++) {
      if (array[i][key] - maxValueObject[key] > 0) {
        maxValueObject = array[i];
      }
    }
    return maxValueObject[key];
  }

  const RPEChartVisible = !!dataIn.metrics.find(
    (metric) => metric.key === "RPE"
  );

  const dataWithRestTravel = useMemo(
    () =>
      addRestAndTravelDates(
        dat,
        ["wellness_score", "RPE", "sPRE"],
        restAndTravelDates
      ),
    [dat, restAndTravelDates]
  );

  useEffect(() => {
    if (dataIn?.wellness_metrics && dataIn?.wellness_metrics.length > 0) {
      const filteredMetrics = dataIn?.wellness_metrics.filter(
        x => x.answer_type !== 'string'
      ).filter((metric) => metric.key !== "wellness_score");

      let otherCharts = [], bodyMapCharts = [], allWellnessCharts = [];
      for (let i = 0; i < filteredMetrics.length; i++) {
        const metric = filteredMetrics[i];
        const chart_data = addRestAndTravelDates(
          dataIn["wellness"][metric.key].map((dt) => {
            const [answerTxt, index] =
              metric.answer_type?.toLowerCase() === "float" ||
              metric.answer_type?.toLowerCase() === "int"
                ? [dt[metric.key], -1]
                : getAnswerByValue(
                    metric.key,
                    Math.round(dt[metric.key])
                  );
            const gameDay = allGames.find(
              (game) => game.date === dt.date
            );
            return {
              ...dt,
              answerTxt,
              answerIndex: index,
              opponent: gameDay?.opponent,
            };
          }),
          [metric.key, "answerIndex"],
          restAndTravelDates
        );
        const maxValue = Math.round(
          findMaxValueObject(chart_data, metric?.key) || 0
        );

        const hasBodyMap = dataIn.bodymap_data?.hasOwnProperty(
          metric.key
        );

        if (hasBodyMap) {
          bodyMapCharts.push(
            <BarChartHighcharts 
              className="wellness-bar-charts width-90" 
              options={{
                ...mapDataForHighcharts({
                  dataIn: {data: chart_data},
                  metric,
                  teamName,
                  games,
                  teamColors,
                  singleAxis: true,
                  isSubjectiveMetric: true,
                  teamMetric,
                  ACWRLowerLimit,
                  ACWRUpperLimit
                }),
                chart:{
                  ...mapDataForHighcharts({
                    dataIn: {data: chart_data},
                    metric,
                    teamName,
                    games,
                    teamColors,
                    singleAxis: true,
                    isSubjectiveMetric: true,
                    teamMetric,
                    ACWRLowerLimit,
                    ACWRUpperLimit
                  })?.chart,
                  height: '100%'
                }
              }}
            ></BarChartHighcharts>
          );
          bodyMapCharts.push(
            <BodyMapView
              playersPage={playersPage}
              title={`${metric.short} Map`}
              data={dataIn.bodymap_data[metric.key]}
              question_key={metric.key}
            />
          );
        } else {
          otherCharts.push(
            <BarChartHighcharts 
              className="wellness-bar-charts"
              options={{
                ...mapDataForHighcharts({
                  dataIn: {data: chart_data},
                  metric,
                  teamName,
                  teamColors,
                  games,
                  singleAxis: true,
                  isSubjectiveMetric: true,
                  teamMetric,
                  ACWRLowerLimit,
                  ACWRUpperLimit
                }),
                chart:{
                  ...mapDataForHighcharts({
                    dataIn: {data: chart_data},
                    metric,
                    teamName,
                    teamColors,
                    games,
                    singleAxis: true,
                    isSubjectiveMetric: true,
                    teamMetric,
                    ACWRLowerLimit,
                    ACWRUpperLimit
                  })?.chart,
                  height: '100%'
                }
              }}
            ></BarChartHighcharts>
          );
        }
      }

      let chartRow = [];
      for (let i = 0; i < otherCharts.length; i++) {
        chartRow.push(
          <div className="width-50 subjective-chart">
            {otherCharts[i]}
          </div>
        );
        if (i > 0 && (i + 1) % 2 == 0) {
          allWellnessCharts.push(
            <div className="subjective-charts-row exportable">
              {chartRow.map(chart => chart)}
            </div>
          )
          chartRow = [];
        }
      }

      if (bodyMapCharts.length > 0) {
        allWellnessCharts.push(
          <div className="subjective-charts-row exportable">
            {bodyMapCharts.map(chart => <div className="width-50 subjective-chart">{chart}</div>)}
          </div>
        )
      }

     setWellnessQestionCharts(allWellnessCharts);
    }
  }, [dataIn?.wellness_metrics]);


  return (
    <>
      <div className="subjective-charts">
        <div className="subjective-charts-row">
          <div class="wellness-stacked-chart width-50">
            <BarChartHighcharts 
                className="wellness-bar-charts width-50"
                options={{
                  ...stackedBarChartDtoToModel({
                    data: dataWithRestTravel,
                    wellnessLabels:
                    label,
                    chartType: 'wellness',
                    games,
                    teamName,
                    showACWR: false,
                    ACWRLowerLimit,
                    ACWRUpperLimit
                }),
                chart: {
                  ...stackedBarChartDtoToModel({
                    data: dataWithRestTravel,
                    wellnessLabels:
                    label,
                    chartType: 'wellness',
                    games,
                    teamName,
                    showACWR: false,
                    ACWRLowerLimit,
                    ACWRUpperLimit
                  })?.chart,
                  height: '100%'
                }
              }}>
            </BarChartHighcharts>
          </div>
          {RPEChartVisible && (
            <div className="rpe-chart exportable">
              <BarChartHighcharts 
                className="wellness-bar-charts width-50"
                options={{
                  ...mapDataForHighcharts({
                    dataIn: {data: dataWithRestTravel, colors: colors_100},
                    metric: {key: "SRPE", short: "RPE Average", name: "Session RPE"},
                    games,
                    teamName,
                    singleAxis: false,
                    isSubjectiveMetric: true,
                    isRPEChart: true,
                    teamColors,
                    teamMetric,
                    ACWRLowerLimit,
                    ACWRUpperLimit
                  }),
                  chart:{
                    ...mapDataForHighcharts({
                      dataIn: {data: dataWithRestTravel, colors: colors_100},
                      metric: {key: "SRPE", short: "RPE Average", name: "Session RPE"},
                      games,
                      teamName,
                      singleAxis: false,
                      isSubjectiveMetric: true,
                      isRPEChart: true,
                      teamColors,
                      teamMetric,
                      ACWRLowerLimit,
                      ACWRUpperLimit
                    })?.chart,
                    height: '100%'
                  }
                }}
              >
              </BarChartHighcharts>
            </div>
          )}
        </div>
        {
          wellnessQuestionCharts && (
            wellnessQuestionCharts.map((chartRow, i) => <React.Fragment key={i}>{chartRow}</React.Fragment>)
          )
        }
      </div>
    </>
  );
}
